import { logError } from './logging';
import { supportsWebP } from './webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/jpeg';

/**
 * Interface for positioning data (all values are percentages)
 */
interface Positioning {
  x: number; // Percentage from left (0-100) - center of mask
  y: number; // Percentage from top (0-100) - center of mask
  scale: number; // Percentage scale (0-100) - percentage of image width
}

/**
 * Options for cropping masked image with padding
 */
interface CropMaskedImageOptions {
  imageFile: File;
  positioning: Positioning;
  maskAspectRatio?: number; // Mask height/width ratio (e.g., 9/16 for 16:9 mask). If not provided, assumes 16:9
  padding?: number; // Default: 10
}

/**
 * Crops a masked image with padding around the mask bounding box and replaces transparency with white background.
 * The image being cropped is 16:9 and 1250px wide.
 * Uses positioning data to calculate the mask bounding box, similar to how applyClipPath.ts calculates mask position.
 *
 * @param options - The cropping options
 * @returns Promise resolving to a JPEG File with white background
 * @throws Error if processing fails
 */
export const cropMaskedImageWithPadding = async ({
  imageFile,
  positioning,
  maskAspectRatio,
  padding = 10
}: CropMaskedImageOptions): Promise<File> => {
  try {
    // Load the image file
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (error) => {
        reject(new Error(`Failed to load image: ${error}`));
      };
      img.src = URL.createObjectURL(imageFile);
    });

    // Use actual image dimensions (image is 16:9, 1250px wide)
    const imageWidth = image.width;
    const imageHeight = image.height;

    // Calculate mask center position from percentages (x, y are center of mask as percentages)
    // Similar to calculatePNGMaskPositioning in pngProcessor.ts
    const maskCenterX = imageWidth * (positioning.x / 100);
    const maskCenterY = imageHeight * (positioning.y / 100);

    // Calculate mask dimensions from scale percentage
    // Scale is a percentage of the image width (e.g., 50 = 50% of image width)
    // Similar to maskAreaWidth calculation in applyPNGMaskToElement
    const maskScalePercent = positioning.scale / 100;
    const maskWidth = imageWidth * maskScalePercent;

    // Calculate mask height using aspect ratio
    // Default to 16:9 (9/16) if not provided, otherwise use provided ratio
    const aspectRatio = maskAspectRatio ?? 9 / 16;
    const maskHeight = maskWidth * aspectRatio;

    // Calculate mask bounding box (the rectangular area the mask occupies)
    // Similar to maskAreaX/Y calculation in applyPNGMaskToElement
    const maskLeft = maskCenterX - maskWidth / 2;
    const maskTop = maskCenterY - maskHeight / 2;
    const maskRight = maskCenterX + maskWidth / 2;
    const maskBottom = maskCenterY + maskHeight / 2;

    // Apply padding around the mask bounding box and clamp to image boundaries
    const cropLeft = Math.max(0, maskLeft - padding);
    const cropTop = Math.max(0, maskTop - padding);
    const cropRight = Math.min(imageWidth, maskRight + padding);
    const cropBottom = Math.min(imageHeight, maskBottom + padding);

    const cropWidth = cropRight - cropLeft;
    const cropHeight = cropBottom - cropTop;

    // Create canvas with white background
    const canvas = document.createElement('canvas');
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cropWidth, cropHeight);

    // Draw the cropped region from the original image
    // This replaces transparency with white where the image is drawn
    ctx.drawImage(image, cropLeft, cropTop, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    // Convert canvas to JPEG blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert canvas to blob'));
          }
        },
        imageType,
        1
      );
    });

    // Create new File with "cropped-" prefix and correct extension
    // The prefix allows downstream code and tests to identify processed images
    const baseName = imageFile.name.replace(/\.[^.]+$/, ''); // Remove extension
    const extension = imageType.split('/').at(1);
    const fileName = `cropped-${baseName}.${extension}`;
    const processedFile = new File([blob], fileName, { type: imageType });

    // Clean up object URL
    URL.revokeObjectURL(image.src);

    return processedFile;
  } catch (error) {
    logError({
      label: 'cropMaskedImageWithPadding',
      error: error instanceof Error ? error : new Error(String(error)),
      message: 'Failed to crop masked image with padding',
      extraData: {
        imageFileName: imageFile.name,
        positioning,
        padding
      }
    });
    throw error;
  }
};
